// FilterComponent.js
import React from 'react';
import {
  TextField,
  Grid,
  Select,
  MenuItem,
  Button,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  Typography,
  InputAdornment,
  IconButton,
  Box,
  Paper,
  Divider
} from '@mui/material';
import {
  Close,
  Search,
  Description,
  Person,
  CalendarToday,
  Phone,
  FilterList,
  CheckCircle,
  Pending,
  Cancel
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled Components
const ModernButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#1976D2',
  color: 'white',
  fontWeight: 600,
  borderRadius: 12,
  padding: '12px 24px',
  textTransform: 'none',
  fontSize: '1rem',
  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
  '&:hover': {
    backgroundColor: '#1565C0',
    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
    transform: 'translateY(-2px)',
  },
  transition: 'all 0.3s ease',
}));

const ModernTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.9)',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#1976D2',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#1976D2',
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    color: '#666',
    '&.Mui-focused': {
      color: '#1976D2',
    },
  },
}));

const ModernSelect = styled(Select)(({ theme }) => ({
  borderRadius: 12,
  backgroundColor: 'rgba(255,255,255,0.9)',
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#1976D2',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#1976D2',
    borderWidth: 2,
  },
}));

const FilterComponentVertical = ({ filters, setFilters, onFilter, idpdvs }) => {

  const handleSelectChange = (event) => {
    const value = event.target.value;
    if (value[value.length - 1] === 'all') {
      setFilters((prevFilters) => ({
        ...prevFilters,
        idpdvs: filters.idpdvs.length === idpdvs.length ? [] : idpdvs.map((user) => user.idpdv),
      }));
    } else {
      setFilters((prevFilters) => ({
        ...prevFilters,
        idpdvs: value,
      }));
    }
  };


  const handleClear = (field) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [field]: ''
    }));
  };


  //const isAllSelected = filters.idpdvs.includes('all');

  const handleSubmit = (e) => {
    e.preventDefault();
    onFilter();
  }

  const isAllSelected = filters.idpdvs.length === idpdvs.length;
  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 3,
        backgroundColor: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.2)',
        maxHeight: '90vh',
        overflow: 'auto'
      }}
    >
      <form onSubmit={handleSubmit}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          pb: 2,
          borderBottom: '1px solid #E0E0E0'
        }}>
          <Search color="primary" sx={{ fontSize: 28 }} />
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 700,
              fontFamily: 'Ubuntu, sans-serif',
              color: '#1976D2'
            }}
          >
            Pesquisar
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ModernTextField
              fullWidth
              label="Descrição"
              variant="outlined"
              value={filters.descricao}
              onChange={(e) => setFilters({ ...filters, descricao: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Description color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => handleClear('descricao')} size="small">
                      {filters.descricao !== '' && <Close />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <ModernTextField
              fullWidth
              label="Nome"
              variant="outlined"
              value={filters.nome}
              onChange={(e) => setFilters({ ...filters, nome: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => handleClear('nome')} size="small">
                      {filters.nome !== '' && <Close />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <ModernTextField
              fullWidth
              label="Data Início"
              type="date"
              variant="outlined"
              value={filters.dataInicio ? filters.dataInicio.split('T')[0] : ''}
              onChange={(e) => {
                const dateValue = e.target.value;
                if (dateValue) {
                  const isoString = `${dateValue}T00:00`;
                  setFilters({ ...filters, dataInicio: isoString });
                } else {
                  setFilters({ ...filters, dataInicio: '' });
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarToday color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => handleClear('dataInicio')} size="small">
                      {filters.dataInicio !== '' && <Close />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '1rem',
                  padding: '14px 14px 14px 0',
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <ModernTextField
              fullWidth
              label="Data Fim"
              type="date"
              variant="outlined"
              value={filters.dataFim ? filters.dataFim.split('T')[0] : ''}
              onChange={(e) => {
                const dateValue = e.target.value;
                if (dateValue) {
                  const isoString = `${dateValue}T23:59`;
                  setFilters({ ...filters, dataFim: isoString });
                } else {
                  setFilters({ ...filters, dataFim: '' });
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarToday color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => handleClear('dataFim')} size="small">
                      {filters.dataFim !== '' && <Close />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '1rem',
                  padding: '14px 14px 14px 0',
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <ModernTextField
              fullWidth
              type='number'
              label="Telefone"
              variant="outlined"
              value={filters.telefone}
              onChange={(e) => setFilters({ ...filters, telefone: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => handleClear('telefone')} size="small">
                      {filters.telefone !== '' && <Close />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          {/* <Grid item xs={10}>
        <TextField
          fullWidth
          label="Descrição, Nome, id, Telefone"
          variant="outlined"
          value={filters.geral}
          onChange={(e) => setFilters({ ...filters, geral: e.target.value })}
          style={{ marginRight: '10px' }}
        />
      </Grid> */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id='status-select-label'>Status</InputLabel>
              <ModernSelect
                labelId='status-select-label'
                label="Status"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                startAdornment={
                  <InputAdornment position="start">
                    <FilterList color="action" />
                  </InputAdornment>
                }
              >
                <MenuItem value="Todos">Todos</MenuItem>
                <MenuItem value="PAGO">RECEBIDO</MenuItem>
                <MenuItem value="Pendente">PENDENTE</MenuItem>
                <MenuItem value="Expirado">EXPIRADO</MenuItem>
              </ModernSelect>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id='transacoes-select-label'>Transações por página</InputLabel>
              <ModernSelect
                labelId='transacoes-select-label'
                label="Transações por página"
                value={filters.itensPagina}
                onChange={(e) => setFilters({ ...filters, itensPagina: e.target.value })}
              >
                <MenuItem value={20}>20 Transações/Página</MenuItem>
                <MenuItem value={50}>50 Transações/Página</MenuItem>
                <MenuItem value={100}>100 Transações/Página</MenuItem>
              </ModernSelect>
            </FormControl>
          </Grid>

          {filters?.idpdvs && (localStorage.getItem('role') === 'ADMIN' || localStorage.getItem('role') === 'SUPER_ADMIN') && (
            <Grid item xs={12}>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputLabel id="idpdvs-select-label" sx={{ backgroundColor: 'white', px: 1 }}>IDPDVs</InputLabel>
                <ModernSelect
                  labelId="idpdvs-select-label"
                  id="idpdvs-select"
                  multiple
                  value={filters.idpdvs}
                  onChange={handleSelectChange}
                  renderValue={(selected) => selected.join(', ')}
                >
                  <MenuItem value="all">
                    <Checkbox checked={isAllSelected} indeterminate={filters.idpdvs.length > 0 && !isAllSelected} />
                    <ListItemText primary="Marcar Todos" />
                  </MenuItem>
                  {idpdvs?.map((user) => (
                    <MenuItem key={user.idpdv} value={user.idpdv}>
                      <Checkbox checked={filters.idpdvs.indexOf(user.idpdv) > -1} />
                      <ListItemText primary={`${user.empresa} - ${user.idpdv}`} />
                    </MenuItem>
                  ))}
                </ModernSelect>
              </FormControl>
            </Grid>
          )}

          {/* Action Button */}
          <Grid item xs={12}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 3,
              pt: 2,
              borderTop: '1px solid #E0E0E0'
            }}>
              <ModernButton fullWidth type='submit'>
                Filtrar
              </ModernButton>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default FilterComponentVertical; 
